import type { Transition, RedirectToResult } from "@uirouter/react";

import { isProduct, isSearch, resolveChildStates } from "app/common/utils/adsTabState";
import { onRedirectAlignPresets } from "app/common/utils/onRedirectAlignPresets";
import { FiltersEnum } from "app/components/filters-bar/utils";
import { AccessWithoutMandatoryParamsHOC } from "app/components/React/HOCs/accessWithoutMandatoryParamsHOC";
import { EAdsTabs } from "app/components/React/PlaResearch/type";
import { SearchAdsWebsitePage } from "app/pages/keyword-research/website-analysis/pages/search-ads/SearchAdsWebsitePage";
import { KeywordCompetitorsOrganicPage } from "app/pages/keyword-research/website-analysis/pages/search-competitors/KeywordCompetitorsOrganicConfig";
import { WebsiteKeywordsPage2Container } from "app/pages/keyword-research/website-analysis/pages/website-keywords/WebsiteKeywordsPage2Container";
import {
    WEBSITE_ANALYSIS_MARKETING_KEYWORDS,
    WEBSITE_ANALYSIS_ROOT,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_ADS,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_COMPETITORS,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_OVERVIEW,
} from "app/pages/sales-intelligence/constants/routes";
import { onEnterPageAnalysis } from "app/pages/website-analysis/config/onEnterPageAnalysis";
import { DEFAULT_CURRENCY } from "app/common/constants/currencies";
import SearchOverview from "app/pages/website-analysis/traffic-sources/search/overview/SearchOverview";
import { filterConsistencyParams } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import { onRedirectSearchAdsPages } from "app/routes/handlers/filter-consistency/pipes/keyword-analysis/onRedirectSearchAdsPages";
import { onRedirectDisplayPages } from "app/routes/handlers/filter-consistency/pipes/website-analysis/onRedirectDisplayPages";
import { onRedirectSERPPages } from "app/routes/handlers/filter-consistency/pipes/website-analysis/onRedirectSERPPages";
import { onRedirectWebsiteAnalysis } from "app/routes/handlers/filter-consistency/pipes/website-analysis/onRedirectWebsiteAnalysis";
import { fallback } from "app/routes/handlers/redirectFunctions/fallback";
import { redirectPipe } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { resolvers } from "single-spa/react-app/config/resolvers/resolvers";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

const trafficOverviewSearchParams: Record<string, unknown> = {
    Keywords_filters: null,
    groupedKeywords_filters: null,
    groupedKeywords_orderby: null,
    Ads_filters: null,
    IncludeOrganic: null,
    IncludePaid: null,
    IncludeBranded: null,
    IncludeNoneBranded: null,
    IncludeNewKeywords: null,
    IncludeTrendingKeywords: null,
    IncludeQuestions: null,
    limits: null,
    BooleanSearchTerms: null,
    search: null,
    selectedDomain: null,
    orderBy: null,
    ExcludeTerms: null,
    ExcludeUrls: null,
    IncludeUrls: null,
    IncludeTerms: null,
    multiIncludeExcludeKeywords: null,
    family: null,
    source: null,
    selectedPhrase: null,
    volumeFromValue: null,
    volumeToValue: null,
    cpcFromValue: null,
    cpcToValue: null,
    difficultyFromValue: null,
    difficultyToValue: null,
    intentsfilter: null,
};

export const SEARCH_GROUP_CONFIG: Record<
    string,
    IRouterState & { isPdfDownloadButton?: boolean; pdfDownloadsMethod?: () => Promise<any>; childStates?: any }
> = {
    [WEBSITE_ANALYSIS_MARKETING_SEARCH_OVERVIEW]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_SEARCH_OVERVIEW,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: `/search-overview/:isWWW/:country/:duration?selectedSingleOverviewGraphTab&selectedNonBrandedTab&webSource?key&performanceOverTimeChartCurrency?${Object.keys(
            trafficOverviewSearchParams,
        ).join(`?`)}`,
        defaultQueryParams: {
            duration: "3m",
            webSource: devicesTypes.TOTAL,
            isWWW: "*",
            performanceOverTimeChartCurrency: DEFAULT_CURRENCY,
        },
        params: {
            performanceOverTimeChartCurrency: { type: "string", dynamic: true },
            ...filterConsistencyParams,
        },
        component: AccessWithoutMandatoryParamsHOC(SearchOverview, "key"),
        redirectTo: redirectPipe(
            fallback({
                digitalsuite: "digitalsuite_website_search_overview",
            }),
            onRedirectWebsiteAnalysis(),
        ) as (transition: Transition) => RedirectToResult,
        stateNameForEductionBar: "competitiveanalysis_website_search_overview",
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "website",
            subSection: "traffic",
            subSubSection: "search-overview",
        },
        trackingId: {
            section: "Competitive Research",
            subSection: "Website Analysis/Search",
            subSubSection: "overview",
        },
        hidePageTitle: true,
        pageTitle: "competitors.search.overview.page.title",
        pageSubtitle: "competitors.search.overview.page.title.info",
        isUSStatesSupported: true,
        reloadOnSearch: true,
        configId: "WebAnalysisSearchOverview",
        configIdDeps: [
            "AudienceOverview",
            "KeywordAnalysis",
            "MobileWebSearch",
            "MobileMarketingMix",
            "MarketingMixTotal",
        ],
        data: {
            trackPageViewOnSearchUpdate: false,
        },
        homeState: "salesIntelligence-home",
    },
    [WEBSITE_ANALYSIS_MARKETING_KEYWORDS]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_KEYWORDS,
        parent: WEBSITE_ANALYSIS_ROOT,
        supportsPageFilter: true,
        url: `/website-keyword-v2/:isWWW/:country/:duration?key&pageFilter&webSource&IncludeBranded&IncludeNoneBranded&multiIncludeExcludeKeywords&multiIncludeExcludeWebsites&volumeFromValue&volumeToValue&cpcFromValue&cpcToValue&Change&source&family&trafficPreset&selectedPageTab`,
        configId: "WebsiteKeywordsV2",
        component: WebsiteKeywordsPage2Container,
        stateNameForEductionBar: "digitalsuite_websiteKWs_page3",
        redirectTo: redirectPipe(onRedirectSERPPages(), onRedirectAlignPresets) as (
            transition: Transition,
        ) => RedirectToResult,
        onEnter: onEnterPageAnalysis,
        resolve: [resolvers.siteInfo],
        pageId: {
            section: "Account Review",
            subSection: "search",
            subSubSection: "keyword",
        },
        trackingId: {
            section: "Account Review",
            subSection: "search",
            subSubSection: "keyword",
        },
        defaultQueryParams: {
            isWWW: "*",
            webSource: devicesTypes.TOTAL,
            selectedPageTab: EKeywordTabs[EKeywordTabs.Total],
        },
        params: {
            isWWW: "*",
            country: "999",
            selectedPageTab: EKeywordTabs[EKeywordTabs.Total],
            ...filterConsistencyParams,
        },
        pageTitle: "competitors.search.keywords.page.title",
        pageSubtitle: "competitors.search.keywords.page.title.info",
        data: {
            trackPageViewOnSearchUpdate: false,
        },
        configIdDeps: ["KeywordAnalysis", "MobileWeb"],
        homeState: "salesIntelligence-home",
        reset: trafficOverviewSearchParams,
        resetOnMainSiteChange: true,
        dynamic: false,
        durationHookV4: true,
        compareButtonText: "website.keywords.2.0.compare.button",
    },
    [WEBSITE_ANALYSIS_MARKETING_SEARCH_COMPETITORS]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_SEARCH_COMPETITORS,
        parent: WEBSITE_ANALYSIS_ROOT,
        url: "/competitors-organic-keywords/:key/:isWWW/:country/:duration?webSource&orderby?selectedSite?websiteType?organicSearchType?risingCompetitors?newCompetitors?category?search",
        reset: {
            orderby: null,
            selectedSite: null,
            websiteType: null,
            organicSearchType: null,
            risingCompetitors: null,
            newCompetitors: null,
            category: null,
            search: null,
        } as Record<string, unknown>,
        component: KeywordCompetitorsOrganicPage,
        resolve: [resolvers.siteInfo],
        configId: "SearchCompetitors",
        pageId: {
            section: "website",
            subSection: "competitors",
            subSubSection: "",
        },
        trackingId: {
            section: "Account Review",
            subSection: "search",
            subSubSection: "competitors",
        },
        pageTitle: "analysis.search.organic.competitors.title",
        pageSubtitle: "analysis.search.organic.competitors.title.tooltip",
        isVirtualSupported: false,
        isUSStatesSupported: true,
        reloadOnSearch: false,
        homeState: "salesIntelligence-home",
        redirectTo: redirectPipe(
            fallback({
                digitalsuite: "organicsearch_websites-competitorsKeywords",
            }),
            onRedirectWebsiteAnalysis(),
        ) as (transition: Transition) => RedirectToResult,
        params: {
            ...filterConsistencyParams,
            duration: "3m",
            webSource: { dynamic: false },
        },
        defaultQueryParams: {
            country: 999,
            isWWW: "*",
        },
        data: {
            filtersConfig: {
                duration: FiltersEnum.DISABLED,
            },
        },
    },
    [WEBSITE_ANALYSIS_MARKETING_SEARCH_ADS]: {
        stateId: WEBSITE_ANALYSIS_MARKETING_SEARCH_ADS,
        parent: WEBSITE_ANALYSIS_ROOT,
        supportsPageFilter: true,
        url: `/website_ads/:isWWW/:country/:duration?pageFilter?key&webSource?${Object.keys(
            trafficOverviewSearchParams,
        ).join(`?`)}&selectedPageTab&multiIncludeExcludeUrl&selectedSite`,
        stateNameForEductionBar: "keywordAnalysis_ads",
        component: SearchAdsWebsitePage,
        resolve: [resolvers.siteInfo],
        redirectTo: redirectPipe(onRedirectDisplayPages(), onRedirectSearchAdsPages) as (
            transition: Transition,
        ) => RedirectToResult,
        onEnter: onEnterPageAnalysis,
        pageId: {
            section: "Acquisition",
            subSection: "Search/Paid Website Analysis",
            subSubSection: "Competitive Analysis/ads",
        },
        trackingId: (toParams) => {
            const selectedPageTab: keyof typeof EAdsTabs = toParams?.selectedPageTab;
            return {
                section: "Keyword Research",
                subSection: "Site Explorer",
                subSubSection: `Search Ads ${selectedPageTab} `,
                subSubSubSection: "Search Ads",
            };
        },
        defaultQueryParams: {
            webSource: "Desktop",
            isWWW: isSearch() ?? "*",
            selectedPageTab: EAdsTabs[EAdsTabs.Text],
        },
        hidePageTitle: true,
        pageTitle: "competitors.search.ads.page.title",
        pageSubtitle: "competitors.search.ads.page.title.info",
        isUSStatesSupported: false,
        data: {
            trackPageViewOnSearchUpdate: false,
        },
        configId: "KeywordAnalysisV2Organic",
        configIdDeps: ["MobileWebSearch"],
        get childStates() {
            return ({ webSource }) => resolveChildStates(webSource);
        },
        reset: isProduct() ?? trafficOverviewSearchParams,
        homeState: "search_websiteanalysis_home",
        params: {
            selectedPageTab: { dynamic: true, value: EAdsTabs[EAdsTabs.Text] },
            isWWW: "*",
            country: "999",
            ...filterConsistencyParams,
        },
    },
};
